/** The universal JTI document code for the fee-waiver request document. */
export const JTI_FEE_WAIVER_DOC_CODE = 'FW001';
/**
 * Resolve the exemption a party is actually requesting. `feeExemptionType`
 * wins when set; the legacy `requestFeesExemption` boolean implies FEE_WAIVER.
 * Mirrors buildFeeExemption() in the JTI envelope builder.
 */
export function effectiveFeeExemption(party) {
    if (party.feeExemptionType && party.feeExemptionType !== 'NONE') {
        return party.feeExemptionType;
    }
    return party.requestFeesExemption ? 'FEE_WAIVER' : 'NONE';
}
/** True when the party's effective exemption is a fee waiver (not GOVT_ENTITY etc.). */
export function partyRequestsFeeWaiver(party) {
    return effectiveFeeExemption(party) === 'FEE_WAIVER';
}
/**
 * True when a document satisfies JTI's fee-waiver connected-document
 * requirement: the universal FW001 code, a name carrying the FW001 form
 * number (seed data), or the exact policy name "Request to Waive Court Fees".
 * Deliberately does NOT match the "(Appeal)" / "(Trnscrpt)" / "Addtl"
 * variants — JTI does not credit those toward the waiver.
 */
export function isFeeWaiverRequestDoc(documentType) {
    if (!documentType)
        return false;
    if (documentType.code === JTI_FEE_WAIVER_DOC_CODE)
        return true;
    const name = (documentType.name || '').trim();
    if (name.includes('FW001'))
        return true;
    return name.toLowerCase() === 'request to waive court fees';
}
/**
 * Vendor-agnostic fee-waiver satisfaction: is the party's fee-waiver
 * requirement met given the envelope's documents? Pure/deterministic — the
 * single shared check the submit gate, the JTI fee-calc emitter, and the
 * completeness checklist collapse onto so they can never disagree.
 *  - effectiveFeeExemption !== FEE_WAIVER → satisfied (NONE/GOVT_ENTITY never need FW-001)
 *  - feeWaiverOnFile === true            → satisfied (existing-waiver carve-out)
 *  - else                                → a connected FW-001 must be present
 */
export function feeWaiverSatisfied(party, documents) {
    if (effectiveFeeExemption(party) !== 'FEE_WAIVER')
        return true;
    if (party.feeWaiverOnFile === true)
        return true;
    return documents.some((d) => isFeeWaiverRequestDoc(d.documentType));
}
/**
 * FWR-11 (party-level trigger): does this filing carry a genuine, satisfied fee
 * waiver on at least one filing party? True iff some party has an effective
 * FEE_WAIVER that is actually satisfied (`feeWaiverOnFile`, or a connected
 * FW-001 among the documents). This is the party-level analogue of the
 * account-level `isFeeWaiverCategory` gate (SOR-152): a self-rep filer on a
 * NORMAL account whose party carries a court fee waiver still pays $0. NONE and
 * GOVT_ENTITY never qualify, and a filer cannot trip it by merely selecting
 * FEE_WAIVER without the on-file flag or the FW-001 — it reuses the same
 * satisfaction rule the submit gate, JTI fee-calc emitter, and completeness
 * checklist share, so the $0 billing can never disagree with them.
 */
export function filingHasSatisfiedFeeWaiver(parties, documents) {
    return parties.some((p) => partyRequestsFeeWaiver(p) && feeWaiverSatisfied(p, documents));
}
// ---------------------------------------------------------------------------
// Account-level fee-waiver default (SOR-152)
// ---------------------------------------------------------------------------
// Filing-party role codes — the plaintiff/petitioner side that files. This
// MIRRORS the PLAINTIFF_ROLES fallback in the JTI stage-3 envelope builder
// (effectiveFilingPartyIds) so the account-level default flips the exemption on
// EXACTLY the parties the envelope treats as filing parties — never a defendant
// (whose contact block would then wrongly emit an exemption). Kept in sync by
// hand deliberately: the envelope pipeline is golden-gated and must not be
// edited for this feature. See stage-3-envelope.ts `PLAINTIFF_ROLES`.
const FILING_PARTY_ROLE_CODES = [
    'PLAIN',
    'PET',
    'CLAIM',
    'XCMPLNT',
    'XPLAIN',
    'INTPLAIN',
    'INTVEN',
    'INTPLD',
];
/** Unwrap a `metadataValues.FILING_PARTY` value the same way the envelope's
 *  `getPartyIds` does: a `string[]` of local roster ids, anything else ignored. */
function filingPartyIdsFromMetadata(value) {
    if (Array.isArray(value))
        return value.filter((v) => typeof v === 'string');
    return [];
}
/**
 * The set of filing-party local ids the envelope would emit for this state —
 * the per-document union of explicit `FILING_PARTY` metadata, falling back to
 * the plaintiff/petitioner roster for any document (or the whole filing) that
 * declares none. This is the SAME identification the stage-3 envelope's
 * `effectiveFilingPartyIds` performs, replicated here so the account-level
 * default and the envelope agree on who the filing parties are.
 */
function resolveFilingPartyIds(state) {
    const ids = new Set();
    const plaintiffIds = state.parties
        .filter((p) => FILING_PARTY_ROLE_CODES.includes(p.roleCode))
        .map((p) => p.id);
    const documents = state.documents ?? [];
    if (documents.length === 0) {
        for (const id of plaintiffIds)
            ids.add(id);
        return ids;
    }
    for (const doc of documents) {
        const declared = filingPartyIdsFromMetadata((doc.metadataValues ?? {}).FILING_PARTY);
        const forThisDoc = declared.length > 0 ? declared : plaintiffIds;
        for (const id of forThisDoc)
            ids.add(id);
    }
    return ids;
}
/**
 * Auto-request the court fee waiver for a filing made from an account-level
 * fee-waiver (Pro Per) account (SOR-152): default each filing party's
 * `feeExemptionType` to `FEE_WAIVER` where the user set NO explicit exemption,
 * so the filer does not have to hand-set the party-level fee exemption on every
 * filing. Pure and total — returns a new `EFilingWizardState` (never mutates)
 * only when it actually changes a party, otherwise the same reference.
 *
 * The account decision is an INJECTED boolean (mirroring
 * `deriveAccountFeeWaiver(category, isFeeWaiverFor)`), so `isFeeWaiverCategory`
 * stays the single account-level fee-waiver derivation — this helper never
 * re-derives the category.
 *
 * The user's choice always wins (never overridden, never duplicated): a party
 * already carrying an explicit `feeExemptionType` (`FEE_WAIVER` or
 * `GOVT_ENTITY`) or the legacy `requestFeesExemption` boolean passes through
 * untouched. Only a party the envelope treats as a filing party
 * (`resolveFilingPartyIds`) is ever defaulted, so a defendant/opposing party is
 * never given an exemption. A non-waiver account (`isFeeWaiverAccount === false`)
 * returns the state unchanged.
 */
export function applyAccountFeeWaiverDefault(state, isFeeWaiverAccount) {
    if (!isFeeWaiverAccount)
        return state;
    const filingPartyIds = resolveFilingPartyIds(state);
    if (filingPartyIds.size === 0)
        return state;
    let changed = false;
    const parties = state.parties.map((party) => {
        if (!filingPartyIds.has(party.id))
            return party;
        // The user's explicit choice wins — an explicit FEE_WAIVER, GOVT_ENTITY, or
        // the legacy requestFeesExemption boolean is never overridden or duplicated.
        const hasExplicitExemption = (party.feeExemptionType && party.feeExemptionType !== 'NONE') ||
            party.requestFeesExemption === true;
        if (hasExplicitExemption)
            return party;
        changed = true;
        return { ...party, feeExemptionType: 'FEE_WAIVER' };
    });
    return changed ? { ...state, parties } : state;
}
